import React from "react";

interface CountDownCounterHookType {
  countDownSeconds: number;
  initializedTime?: number; // Date.now() in milliseconds
  onCounterUpdate?: CountDownCounterUpdateCallBackType;
}

export type CountDownCounterUpdateCallBackType = (props: { secondsLeft: number; secondsPassed: number }) => void;

export default function useCountDownTimerHook({ onCounterUpdate, ...props }: CountDownCounterHookType) {
  const getInitialTimeLeftValues = React.useCallback(() => {
    if (props.initializedTime) {
      return props.countDownSeconds - (Date.now() - props.initializedTime) / 1000;
    } else {
      return props.countDownSeconds;
    }
  }, [props.initializedTime, props.countDownSeconds]);

  const [secondsLeft, setSecondsLeft] = React.useState<number>(() => {
    return getInitialTimeLeftValues();
  });

  const initializeCounter = React.useCallback(() => {
    const startDate = props.initializedTime ? props.initializedTime : Date.now();
    setSecondsLeft(props.countDownSeconds);
    const timerId = setInterval(() => {
      const delta = Math.floor((Date.now() - startDate) / 1000);
      setSecondsLeft(props.countDownSeconds - delta);
      onCounterUpdate?.({ secondsPassed: delta, secondsLeft: props.countDownSeconds - delta });
      if (props.countDownSeconds - delta <= 0) {
        clearInterval(timerId);
      }
    }, 1000);
    return timerId;
  }, [onCounterUpdate, props.countDownSeconds, props.initializedTime]);

  React.useEffect(() => {
    const timerId = initializeCounter();
    return () => clearInterval(timerId);
  }, [initializeCounter]);

  const reInitialiseTimer = React.useCallback(() => {
    setSecondsLeft(getInitialTimeLeftValues());
    return initializeCounter();
  }, [initializeCounter, getInitialTimeLeftValues]);

  return { secondsLeft, reInitialiseTimer };
}
